import { loadGoogleMaps } from '../helpers/maps'
import { ICoordinate } from '../models'

export interface IPlaceByCoordinates {
  id?: string
  location: { lat: number, lng: number }
  name: string
}

const DEGREE_IN_METERS = 111320

// The Places/Geocoding REST endpoints send no CORS headers, so a browser can
// never call them directly — the JS library is the only option here.
const importLibrary = async (name: string): Promise<any> => {
  await loadGoogleMaps()
  return (window as any).google.maps.importLibrary(name)
}

const geocode = async (request: google.maps.GeocoderRequest): Promise<google.maps.GeocoderResult[]> => {
  await importLibrary('geocoding')
  const { results } = await new google.maps.Geocoder().geocode(request)
  return results
}

const findComponent = (components: google.maps.GeocoderAddressComponent[] | undefined, ...types: string[]) =>
  components?.find(component => types.some(type => component.types.includes(type)))

class GoogleMapsService {
  public getPlaces = async (
    nameToSearch: string,
    bias?: { center: ICoordinate, radius?: number }
  ): Promise<{ id: string, name: string }[]> => {
    try {
      const { AutocompleteService } = await importLibrary('places')
      // strictBounds is supported by the API but missing from the typings.
      const request: google.maps.places.AutocompletionRequest & { strictBounds?: boolean } = {
        input: nameToSearch
      }

      // The app restricts results to the service area rather than merely
      // ranking them, so a street name elsewhere never wins the first slot.
      if (bias?.radius) {
        const latitudeSpan = bias.radius / DEGREE_IN_METERS
        const longitudeSpan = latitudeSpan / Math.cos(bias.center.latitude * Math.PI / 180)

        request.bounds = new google.maps.LatLngBounds(
          { lat: bias.center.latitude - latitudeSpan, lng: bias.center.longitude - longitudeSpan },
          { lat: bias.center.latitude + latitudeSpan, lng: bias.center.longitude + longitudeSpan }
        )
        request.strictBounds = true
      }

      const response = await new AutocompleteService().getPlacePredictions(request)

      return (response.predictions || []).map((prediction: google.maps.places.AutocompletePrediction) => ({
        id: prediction.place_id,
        name: prediction.description
      }))
    } catch (error) {
      console.error(error)
      return []
    }
  }

  public getCoordinatesPlace = async (placeId: string): Promise<Partial<ICoordinate>> => {
    try {
      const [result] = await geocode({ placeId })
      if (!result) return {}

      return {
        latitude: result.geometry.location.lat(),
        longitude: result.geometry.location.lng()
      }
    } catch (error) {
      console.error(error)
      return {}
    }
  }

  public getPlaceByCoordinates = async ({ latitude, longitude }: ICoordinate): Promise<IPlaceByCoordinates | null> => {
    try {
      const results = await geocode({ location: { lat: latitude, lng: longitude } })
      const place = results.find(result =>
        result.types.includes('street_address') || result.types.includes('plus_code')
      ) ?? results[0]
      if (!place) return null

      const components = place.address_components
      const street = findComponent(components, 'route', 'plus_code')
      const streetNumber = findComponent(components, 'street_number')
      const locality = findComponent(components, 'locality', 'political')
      const name = [street?.long_name, streetNumber?.long_name, locality?.long_name]
        .filter(Boolean)
        .join(', ')

      return {
        id: place.place_id,
        location: { lat: latitude, lng: longitude },
        name: name || place.formatted_address
      }
    } catch (error) {
      console.error(error)
      return null
    }
  }
}

export default new GoogleMapsService()
